/**
 * Creator fee-claim helpers for DAMM v2 positions.
 *
 * Our pools use CollectFeeMode.OnlyB at creation, so fees accrue in the
 * quote token (SOL) only. There's no token-side fee to claim, and no burn
 * tx needed — the creator's claim lands pure SOL in their wallet.
 *
 * Returns { claimSig, solGained } so the UI can show the exact lamport delta.
 */

/* global BigInt */
import {
  PublicKey,
  Transaction,
  ComputeBudgetProgram,
} from "@solana/web3.js";
import {
  TOKEN_PROGRAM_ID,
  NATIVE_MINT,
  createAssociatedTokenAccountIdempotentInstruction,
  getAssociatedTokenAddressSync,
  createBurnCheckedInstruction,
} from "@solana/spl-token";
import BN from "bn.js";
import { sendTx } from "./sendTx";
import { SLURP_TREASURY } from "./solanaLaunch";

const loadCpAmm = async () => {
  const mod = await import("@meteora-ag/cp-amm-sdk");
  return mod;
};

/**
 * Read the unclaimed fees on a position.
 * The position's stored `feeBPending` only updates when the position is
 * modified or fees are claimed — it's a checkpoint, not the live pending
 * amount. For the true live pending fee we combine pool's global fee-per-token
 * accumulator with the position's checkpoint via `getUnClaimLpFee`.
 */
export async function readPendingFees({ connection, positionAddress, poolAddress }) {
  const { CpAmm, getUnClaimLpFee } = await loadCpAmm();
  const cpAmm = new CpAmm(connection);
  const position = new PublicKey(positionAddress);
  const posState = await cpAmm.fetchPositionState(position);
  const poolState = await cpAmm.fetchPoolState(
    poolAddress ? new PublicKey(poolAddress) : posState.pool
  );
  const { feeTokenA, feeTokenB } = getUnClaimLpFee(poolState, posState);
  return {
    pendingSolRaw: feeTokenB.toString(),
    pendingTokenRaw: feeTokenA.toString(),
  };
}

/**
 * Claim all accrued fees on the creator's position.
 * With CollectFeeMode.OnlyB, this returns pure SOL to the creator's wallet.
 * No token-side to handle → no burn tx needed.
 *
 * @returns { claimSig, solGained } — lamport delta on creator's native balance
 */
export async function claimFees({
  connection,
  wallet,
  poolAddress,
  positionAddress,
  positionNftMint,
}) {
  if (!wallet?.publicKey) throw new Error("Wallet not connected");
  const { CpAmm, derivePositionNftAccount } = await loadCpAmm();
  const cpAmm = new CpAmm(connection);

  const pool = new PublicKey(poolAddress);
  const position = new PublicKey(positionAddress);
  const nftMint = new PublicKey(positionNftMint);
  const nftAccount = derivePositionNftAccount(nftMint);

  const poolState = await cpAmm.fetchPoolState(pool);
  const preSolLamports = BigInt(
    await connection.getBalance(wallet.publicKey, "confirmed")
  );

  const claimTx = await cpAmm.claimPositionFee({
    owner: wallet.publicKey,
    position,
    pool,
    positionNftAccount: nftAccount,
    tokenAMint: poolState.tokenAMint,
    tokenBMint: poolState.tokenBMint,
    tokenAVault: poolState.tokenAVault,
    tokenBVault: poolState.tokenBVault,
    tokenAProgram: TOKEN_PROGRAM_ID,
    tokenBProgram: TOKEN_PROGRAM_ID,
  });
  const claimSig = await sendTx(connection, wallet, claimTx);

  const postSolLamports = BigInt(
    await connection.getBalance(wallet.publicKey, "confirmed")
  );
  const solGained =
    postSolLamports > preSolLamports
      ? (postSolLamports - preSolLamports).toString()
      : "0";

  return { claimSig, solGained };
}

/**
 * Burn-mode claim. Instead of pocketing the SOL fees, the creator's share
 * is swapped into the token via the pool and burned in a single tx:
 *   1. claimPositionFee (SDK adds ATA + unwrap; we strip the unwrap so
 *      the fees stay in the creator's WSOL ATA)
 *   2. cpAmm.swap — WSOL → token on the SAME pool (tight 1% slippage)
 *   3. burnChecked — burn the swap's min-out from the creator's token ATA
 *   4. closeAccount(WSOL ATA) — unwraps any leftover back to creator
 *
 * @returns { claimSig, solSpent, tokensBurnedRaw }
 */
export async function claimAndBurnFees({
  connection,
  wallet,
  poolAddress,
  positionAddress,
  positionNftMint,
  tokenMint,
  tokenDecimals,
}) {
  if (!wallet?.publicKey) throw new Error("Wallet not connected");
  const {
    CpAmm,
    derivePositionNftAccount,
    swapQuoteExactInput,
    getCurrentPoint,
  } = await loadCpAmm();
  const cpAmm = new CpAmm(connection);

  const owner = wallet.publicKey;
  const pool = new PublicKey(poolAddress);
  const position = new PublicKey(positionAddress);
  const nftMint = new PublicKey(positionNftMint);
  const nftAccount = derivePositionNftAccount(nftMint);
  const mint = new PublicKey(tokenMint);

  const poolState = await cpAmm.fetchPoolState(pool);
  const posState = await cpAmm.fetchPositionState(position);

  // Live pending SOL fees from the position accumulator. Use this as the
  // amountIn for the swap — this is the safe lower bound; the actual
  // claim may be marginally larger if another trade lands in the same
  // block, in which case the leftover WSOL unwraps via closeAccount.
  const { getUnClaimLpFee } = await loadCpAmm();
  const { feeTokenB } = getUnClaimLpFee(poolState, posState);
  if (feeTokenB.lten(0)) {
    throw new Error("No pending SOL fees to burn");
  }
  const swapAmountIn = new BN(feeTokenB.toString());

  // Quote swap: feeTokenB (SOL) → token. On our launches token_is_x=true,
  // so "a" is the token and "b" is SOL. Swap is BtoA (SOL → token).
  // Slippage is in basis points — SDK does `factor / 10000`, so 100 = 1%.
  const currentPoint = await getCurrentPoint(connection, poolState.activationType);
  const SLIPPAGE_BPS = 100; // 1%
  const quote = swapQuoteExactInput(
    poolState,
    currentPoint,
    swapAmountIn,
    SLIPPAGE_BPS,
    false, // aToB=false → BtoA (SOL → token)
    false, // no referral
    tokenDecimals,
    9 // SOL decimals
  );
  const minOutRaw = quote.minimumAmountOut;
  if (!minOutRaw || minOutRaw.lten(0)) {
    throw new Error("Swap quote returned zero min-out");
  }

  // Build claim + swap txs via the SDK — each one already adds its own
  // SOL wrap/unwrap ixs. Flow end-to-end:
  //   claim (fees → WSOL ATA → unwrap to creator's SOL)
  //   swap (wrap amountIn SOL → swap → unwrap leftover WSOL)
  //   burn (burn minOut from creator's token ATA)
  // We only strip the SDK's compute-budget ixs so we can set our own once.
  const claimTx = await cpAmm.claimPositionFee({
    owner,
    position,
    pool,
    positionNftAccount: nftAccount,
    tokenAMint: poolState.tokenAMint,
    tokenBMint: poolState.tokenBMint,
    tokenAVault: poolState.tokenAVault,
    tokenBVault: poolState.tokenBVault,
    tokenAProgram: TOKEN_PROGRAM_ID,
    tokenBProgram: TOKEN_PROGRAM_ID,
  });
  const claimIxs = claimTx.instructions.filter(
    (ix) => !ix.programId.equals(ComputeBudgetProgram.programId)
  );

  // Host-fee kickback ATA — 20% of Meteora's protocol fee on this swap
  // routes to slurp treasury's WSOL ATA. Costs nothing extra; the user
  // still pays the same 1% pool fee. See solanaSwap.js for full explainer.
  const slurpHostAta = getAssociatedTokenAddressSync(
    NATIVE_MINT,
    new PublicKey(SLURP_TREASURY),
    true,
  );
  const swapTx = await cpAmm.swap({
    payer: owner,
    pool,
    inputTokenMint: NATIVE_MINT,
    outputTokenMint: mint,
    amountIn: swapAmountIn,
    minimumAmountOut: minOutRaw,
    tokenAMint: poolState.tokenAMint,
    tokenBMint: poolState.tokenBMint,
    tokenAVault: poolState.tokenAVault,
    tokenBVault: poolState.tokenBVault,
    tokenAProgram: TOKEN_PROGRAM_ID,
    tokenBProgram: TOKEN_PROGRAM_ID,
    referralTokenAccount: slurpHostAta,
    poolState,
  });
  const swapIxs = swapTx.instructions.filter(
    (ix) => !ix.programId.equals(ComputeBudgetProgram.programId)
  );
  // Idempotent create for slurp's host ATA — prepended below.
  const slurpHostAtaCreateIx = createAssociatedTokenAccountIdempotentInstruction(
    owner,
    slurpHostAta,
    new PublicKey(SLURP_TREASURY),
    NATIVE_MINT,
  );

  // Burn the swap's minimum-out from the creator's token ATA. Any output
  // beyond minOutRaw stays in the creator's ATA (a tiny slippage dust).
  const creatorTokenAta = getAssociatedTokenAddressSync(mint, owner);
  const burnIx = createBurnCheckedInstruction(
    creatorTokenAta,
    mint,
    owner,
    BigInt(minOutRaw.toString()),
    tokenDecimals
  );

  const tx = new Transaction();
  tx.add(
    ComputeBudgetProgram.setComputeUnitLimit({ units: 600_000 }),
    ComputeBudgetProgram.setComputeUnitPrice({ microLamports: 50_000 }),
    // Ensure the token ATA exists — the swap outputs into it.
    createAssociatedTokenAccountIdempotentInstruction(
      owner,
      creatorTokenAta,
      owner,
      mint
    ),
    // Slurp host-fee ATA — idempotent so this no-ops after first use.
    slurpHostAtaCreateIx,
    ...claimIxs,
    ...swapIxs,
    burnIx
  );
  tx.feePayer = owner;

  const claimSig = await sendTx(connection, wallet, tx);

  return {
    claimSig,
    solSpent: swapAmountIn.toString(),
    tokensBurnedRaw: minOutRaw.toString(),
  };
}
